/**
 * PostHog wrapper. No-op when VITE_POSTHOG_KEY is unset (dev without analytics).
 *
 * The bulk of event capture happens server-side (from inside agent tool
 * handlers) — see clubbot/analytics.py. The frontend's job is just:
 *   - init the lib so pageviews + autocapture work
 *   - identify() the user once we know who they are
 *   - reset() on sign-out so the next session doesn't inherit identity
 *   - track() for the few interactions that never reach the server
 *     (e.g. availability club-grid taps, feature dismissals)
 */

import posthog from "posthog-js";

let _initialized = false;

export function initAnalytics(): void {
  if (_initialized) return;
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return;
  const host = import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";
  posthog.init(key, {
    api_host: host,
    person_profiles: "identified_only",
    capture_pageview: true,
    autocapture: false, // we don't want noisy click events
    disable_session_recording: true,
    ip: false, // privacy: skip IP collection
  });
  _initialized = true;
}

export function identifyUser(userId: string, email: string): void {
  if (!_initialized) return;
  posthog.identify(userId, {
    email_domain: email.split("@")[1] ?? null,
  });
}

export function resetAnalytics(): void {
  if (!_initialized) return;
  posthog.reset();
}

/** Client-side event for interactions that never hit the server. Prefer
 * server-side capture() when a request exists — this is the exception path. */
export function track(event: string, properties?: Record<string, unknown>): void {
  if (!_initialized) return;
  posthog.capture(event, properties);
}
